import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin } from "lucide-react";
import { formatEventDate, type EventRow } from "@/lib/events";

export function EventBanner() {
  const { data: ev } = useQuery({
    queryKey: ["featured_event"],
    queryFn: async () => {
      const { data } = await supabase
        .from("events")
        .select("*")
        .eq("is_featured", true)
        .neq("status", "draft")
        .neq("status", "cancelled")
        .gte("starts_at", new Date().toISOString())
        .order("starts_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      return (data as EventRow | null) ?? null;
    },
  });

  if (!ev) return null;

  return (
    <section className="container mx-auto px-4 py-10">
      <div className="mx-auto flex max-w-5xl flex-col gap-5 rounded-3xl border border-accent/30 bg-gradient-to-br from-accent/10 via-background to-primary/10 p-6 shadow-card md:flex-row md:items-center md:justify-between md:p-8">
        <div className="flex items-start gap-4">
          <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <CalendarDays className="h-6 w-6" aria-hidden />
          </div>
          <div className="min-w-0">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
              Upcoming event
            </span>
            <h2 className="mt-1 font-display text-2xl font-semibold md:text-3xl">{ev.title}</h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>{formatEventDate(ev.starts_at, ev.ends_at)}</span>
              {(ev.location_name || ev.is_virtual) && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {ev.is_virtual ? "Online" : ev.location_name}
                </span>
              )}
            </div>
            {ev.summary && (
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {ev.summary}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button asChild size="lg" className="rounded-full">
            <Link to={`/events/${ev.slug}`}>RSVP</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full">
            <Link to="/events">All events</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
